/* eslint-disable max-lines */
export const en: Record<string, string> = {
  'app.title': 'AutoAcademy',
  'app.subtitle': 'Learn to drive for real: manual and automatic.',
  'app.tagline': 'Smoothness, observation and safety. Never speed.',

  // Menu
  'menu.continue': 'Continue',
  'menu.levels': 'Levels',
  'menu.freeDrive': 'Free drive',
  'menu.theory': 'Theory (Module 0)',
  'menu.settings': 'Settings',
  'menu.controls': 'Controls',
  'menu.track.auto': 'Automatic car',
  'menu.track.manual': 'Manual car',
  'menu.track.auto.sub': 'P·R·N·D and two pedals: focus on the road',
  'menu.track.manual.sub': 'Clutch, H-pattern shifter and the bite point',
  'menu.trackDone': 'Track completed · Free drive',
  'levels.title': 'Level curriculum',
  'levels.locked': 'Locked: pass the previous level',
  'levels.best': 'Best',
  'levels.xp': 'XP',
  'levels.play': 'Practice',
  'levels.rank.rookie': 'Rookie',
  'levels.rank.learner': 'Learner driver',
  'levels.rank.solid': 'Confident driver',
  'levels.rank.expert': 'Expert driver',

  // Settings
  'settings.title': 'Settings',
  'settings.language': 'Language',
  'settings.drivingSide': 'Driving side',
  'settings.right': 'Right (EU/US)',
  'settings.left': 'Left (UK/JP)',
  'settings.units': 'Units',
  'settings.audio': 'Sound',
  'settings.on': 'On',
  'settings.off': 'Off',
  'settings.camera': 'Default camera',
  'settings.chase': 'Third person',
  'settings.cockpit': 'Cockpit',
  'settings.reset': 'Erase progress',
  'settings.resetConfirm': 'Are you sure? All your progress will be erased.',
  'settings.back': 'Back',

  // Cars
  'car.sedan.name': 'Sedan',
  'car.city.name': 'City car',
  'car.ev.name': 'Electric',
  'car.mcity.name': 'Manual city car',
  'car.mdiesel.name': 'Diesel van',
  'car.msport.name': 'Sports coupé',
  'car.select': 'Vehicle',
  'car.locked': 'Unlocked by progressing',

  // Levels
  'level.l1.title': 'Level 1 · Moving off and stopping',
  'level.l1.desc':
    'Your first time behind the wheel: seatbelt, brake, D, and the car creeps forward on its own. Accelerate gently to 20 km/h and stop without jerking.',
  'level.l2.title': 'Level 2 · Steering and lane keeping',
  'level.l2.desc':
    'Drive a full lap of the circuit while staying inside the lane. Look far ahead, calm hands, small corrections.',
  'level.l3.title': 'Level 3 · Stopping on the mark',
  'level.l3.desc':
    'Three stop lines: modulate the brake to stop with your nose exactly on each line. Brake early and softly, fine-tune at the end.',
  'level.l4.title': 'Level 4 · Turns with observation',
  'level.l4.desc':
    'Mirrors – signal – manoeuvre. Stop at the line, signal and turn right; come back and turn left. The examiner watches your eyes, not just your line.',
  'level.l5.title': 'Level 5 · Reversing',
  'level.l5.desc':
    'Reverse straight down the cone corridor and back into the parking bay. Slowly: in reverse, speed is your enemy.',
  'level.free.title': 'Free drive',
  'level.free.desc':
    'No goals, no scoring. Practice whatever you like: the circuit, the slalom, the parking bay or the hill-start ramp.',

  // MANUAL levels
  'level.m1.title': 'Manual 1 · The bite point',
  'level.m1.desc':
    'The most important lesson of stick shift: feel where the clutch "bites". Hold the car almost still with the pedal alone, no brake and no stalling.',
  'level.m2.title': 'Manual 2 · Moving off without stalling',
  'level.m2.desc':
    'The two-foot dance: bite point, a whisper of throttle, and ease it out. Three clean launches in a row, each one smoother.',
  'level.m3.title': 'Manual 3 · Stopping without stalling',
  'level.m3.desc':
    'Braking is easy; pressing the clutch at the right moment is the skill. Three stop lines: if the car shudders, you were late.',
  'level.m4.title': 'Manual 4 · Shifting up',
  'level.m4.desc':
    'From 1st to 4th following the tachometer and your ears: clutch down, shift, release while feeding throttle. No jerks, no over-revving.',
  'level.m5.title': 'Manual 5 · Downshifts and engine braking',
  'level.m5.desc':
    'A 30 zone ahead: shift down in time and let the engine slow you. Finish stopped at the line in the right gear.',
  'level.m6.title': 'Manual 6 · Hill start',
  'level.m6.desc':
    'The classic manual test. Stop on the slope and pull away with handbrake, bite point and throttle — without rolling back half a meter.',
  'level.m7.title': 'Manual 7 · The clutch exam',
  'level.m7.desc':
    'A full course with a STOP included: right gears, fine clutch work, observation and zero stalls. Pass it and the manual is yours.',
  'level.freeManual.title': 'Manual practice ground',
  'level.freeManual.desc':
    'Free practice with the manual car: 9% and 16% ramps for hill starts, a cone slalom and a parking bay. No scoring.',

  // MANUAL objectives
  'obj.m1.gear1': 'Press the clutch (Shift) and engage 1st',
  'obj.m1.bite': 'Hold the car 3 s at the bite point (no brake)',
  'obj.m1.go': 'Release fully, with a little throttle, and move off',
  'obj.m1.stop': 'Clutch in and brake: stop without stalling',
  'obj.m1.neutral': 'Leave the stick in neutral (N)',
  'obj.m2.gear1': 'Clutch down and 1st',
  'obj.m2.go15': 'Pull away smoothly up to 15 km/h',
  'obj.m2.stop': 'Stop without stalling (clutch before you halt)',
  'obj.m2.again1': 'Again: clutch and 1st',
  'obj.m2.again2': 'Last one: clutch and 1st',
  'obj.m2.go20': 'Pull away and reach 20 km/h',
  'obj.m2.neutral': 'Neutral (N) to finish',
  'obj.m3.gear1': 'Clutch and 1st',
  'obj.m3.go': 'Accelerate to 20 km/h',
  'obj.m3.go25': 'Accelerate to 25 km/h',
  'obj.m3.stopA': 'Stop at line A without stalling',
  'obj.m3.stopB': 'Stop at line B without stalling',
  'obj.m3.stopC': 'Stop at line C without stalling',
  'obj.m3.neutral': 'Neutral (N)',
  'obj.m4.gear1': 'Clutch and 1st',
  'obj.m4.go': 'Pull away to 15 km/h',
  'obj.m4.gear2': 'Shift to 2nd (clutch down, stick, release)',
  'obj.m4.sp30': 'Accelerate in 2nd to 30 km/h',
  'obj.m4.gear3': 'Shift to 3rd',
  'obj.m4.sp45': 'Accelerate to 45 km/h',
  'obj.m4.gear4': 'Shift to 4th',
  'obj.m4.sp60': 'Reach 58 km/h in 4th',
  'obj.m4.stop': 'Brake and stop (clutch at the end)',
  'obj.m4.neutral': 'Neutral (N)',
  'obj.m5.gear1': 'Clutch and 1st',
  'obj.m5.go': 'Pull away to 15 km/h',
  'obj.m5.gear3': 'Shift up to 3rd',
  'obj.m5.sp50': 'Accelerate to ~50 km/h',
  'obj.m5.gear4': 'Shift to 4th',
  'obj.m5.down3': '30 zone ahead: downshift to 3rd (under 45 km/h)',
  'obj.m5.down2': 'Downshift to 2nd and let the engine hold you (under 32 km/h)',
  'obj.m5.stopLine': 'Stop at the STOP line',
  'obj.m5.neutral': 'Neutral (N)',
  'obj.m6.gear1': 'Mirrors, clutch and 1st',
  'obj.m6.base': 'Approach the base of the hill',
  'obj.m6.stopSlope': 'FULL stop inside the marked zone on the slope',
  'obj.m6.hill': 'Pull away uphill without rolling back more than 40 cm',
  'obj.m6.top': 'Crest the hill onto the plateau',
  'obj.m6.stopTop': 'Stop at the top',
  'obj.m6.neutral': 'Neutral and handbrake',
  'obj.m7.gear1': 'Mirrors, clutch and 1st',
  'obj.m7.lapA': 'Drive the circuit to the STOP, in the right gear',
  'obj.m7.stop1': 'Come to a complete stop at the STOP line',
  'obj.m7.resume': 'Move off again without stalling',
  'obj.m7.lapB': 'Complete the lap to the finish',
  'obj.m7.stop2': 'Come to a complete stop',
  'obj.m7.neutral': 'Neutral (N): exam finished',

  // Objectives
  'obj.l1.prep': 'Buckle up (B), check mirrors, press the brake and shift to D',
  'obj.l1.go': 'Accelerate gently to 20 km/h',
  'obj.l1.stop': 'Come to a complete, smooth stop',
  'obj.l1.park': 'Shift to P to park',
  'obj.l2.prep': 'Belt, mirrors, brake and D',
  'obj.l2.lap': 'Complete one lap without leaving the lane',
  'obj.l2.stop': 'Come to a complete stop',
  'obj.l2.park': 'Shift to P',
  'obj.l3.prep': 'Belt, mirrors, brake and D',
  'obj.l3.go': 'Reach 25 km/h',
  'obj.l3.go2': 'Reach 30 km/h',
  'obj.l3.stopA': 'Stop with your nose on line A',
  'obj.l3.stopB': 'Stop with your nose on line B',
  'obj.l3.stopC': 'Stop with your nose on line C',
  'obj.l3.park': 'Shift to P',
  'obj.l4.prep': 'Belt, mirrors, brake and D',
  'obj.l4.approach': 'Approach the junction calmly',
  'obj.l4.stop1': 'Signal right, check mirrors and stop at the line',
  'obj.l4.turnR': 'Turn right following the lane',
  'obj.l4.loop': 'Loop around at the end of the road',
  'obj.l4.stop2': 'Signal left, check mirrors and stop at the yield line',
  'obj.l4.turnL': 'Turn left and merge',
  'obj.l4.stop3': 'Come to a complete stop',
  'obj.l4.park': 'Shift to P',
  'obj.l5.prepR': 'Check ALL THREE mirrors, press the brake and engage R',
  'obj.l5.backA': 'Reverse straight into the marked zone',
  'obj.l5.fwd': 'Brake and shift back to D',
  'obj.l5.preBay': 'Drive forward past the parking bay',
  'obj.l5.prepR2': 'Right and rear mirrors, then engage R',
  'obj.l5.bay': 'Reverse park inside the bay',
  'obj.l5.park': 'Shift to P: parked',

  // HUD
  'hud.objectives': 'Objectives',
  'hud.time': 'Time',
  'hud.collisions': 'Hits',
  'hud.abs': 'ABS',
  'hud.tc': 'TC',
  'hud.hillhold': 'HOLD',
  'hud.handbrake': 'HANDBRAKE',
  'hud.belt': 'SEATBELT',
  'hud.shifting': 'Shifting…',
  'hud.mirror.left': 'Left mirror',
  'hud.mirror.right': 'Right mirror',
  'hud.mirror.rear': 'Rear-view mirror',
  'hud.reverseCam': 'Reversing camera',

  // Touch controls
  'touch.controls': 'Touch driving controls',
  'touch.brake': 'BRAKE',
  'touch.throttle': 'THROTTLE',
  'touch.steer.left': 'Steer left',
  'touch.steer.right': 'Steer right',
  'touch.signal.left': 'Left turn signal',
  'touch.signal.right': 'Right turn signal',
  'touch.mirror.left': 'LEFT',
  'touch.mirror.rear': 'REAR',
  'touch.mirror.right': 'RIGHT',
  'touch.gear': 'Gear',

  // Requirement hints
  'hint.signal.left': 'Signal LEFT (Q)',
  'hint.signal.right': 'Signal RIGHT (E)',
  'hint.glance.left': 'Check the left mirror (1)',
  'hint.glance.right': 'Check the right mirror (3)',
  'hint.glance.rear': 'Check the rear-view mirror (2)',
  'hint.slowDown': 'Slow down',

  // Warnings
  'warn.coneHit': 'You clipped a cone',
  'warn.collision': 'Collision!',
  'warn.harshAccel': 'Harsh acceleration: ease the pedal',
  'warn.harshBrake': 'Harsh braking: brake earlier and softer',
  'warn.seatbelt': 'Fasten your seatbelt (B)',
  'warn.speeding': 'Speeding',
  'warn.laneDeparture': 'You are drifting out of the lane',
  'deny.brakeRequired': 'Press the brake to shift out of P',
  'deny.moving': 'You cannot engage that gear while moving',
  'deny.shifting': 'Wait for the shift',

  // Debrief
  'debrief.passed': 'Level passed!',
  'debrief.failed': 'Level not passed',
  'debrief.score': 'Score',
  'debrief.smoothness': 'Smoothness',
  'debrief.observation': 'Observation',
  'debrief.rules': 'Rules',
  'debrief.speed': 'Speed',
  'debrief.collisions': 'Collisions',
  'debrief.time': 'Time',
  'debrief.tips': 'Instructor tips',
  'debrief.failures': 'Reasons',
  'debrief.retry': 'Retry',
  'debrief.next': 'Next level',
  'debrief.menu': 'Menu',
  'debrief.xp': 'XP earned',

  'fail.collisions': 'Too many collisions',
  'fail.timeout': 'Time ran out',
  'fail.incomplete': 'Objectives incomplete',
  'fail.harshAccel': 'Too many harsh accelerations',
  'fail.harshBrake': 'Too many harsh brakes',
  'fail.observation': 'Insufficient observation (mirrors)',
  'fail.speeding': 'Repeated speeding',
  'fail.lowScore': 'Overall score too low',

  'tip.brakeEarlier':
    'Brake earlier with less pressure: press, hold, and release at the very end to stop without a jerk.',
  'tip.progressiveThrottle':
    'Accelerate as if there were an egg under the pedal: progressive pressure, no stabs.',
  'tip.mirrors':
    'Before any manoeuvre: rear-view mirror, side mirror, blind spot. Make looking a habit.',
  'tip.signals': 'Always signal before turning or changing lanes, with enough notice.',
  'tip.references': 'Use references: cones, lines and mirrors tell you where your wheels are.',
  'tip.seatbelt': 'Seatbelt on, always, before the car moves.',
  'tip.great': 'Clean driving. That is how it is done: smooth, observant and predictable.',

  // Pause
  'pause.title': 'Paused',
  'pause.resume': 'Resume',
  'pause.restart': 'Restart level',
  'pause.exit': 'Exit to menu',

  // Level intro
  'intro.theory': 'Before you drive',
  'intro.objectives': 'Level objectives',
  'intro.start': 'Drive',
  'intro.back': 'Back',
  'intro.car': 'Vehicle',

  // Controls
  'controls.title': 'Controls',
  'controls.keyboard': 'Keyboard',
  'controls.gamepad': 'Gamepad',
  'controls.throttle': 'Accelerate',
  'controls.brake': 'Brake',
  'controls.steer': 'Steering',
  'controls.gears': 'Selector P · R · N · D',
  'controls.handbrake': 'Handbrake',
  'controls.signals': 'Turn signals left/right',
  'controls.glance': 'Mirrors: left / rear-view / right',
  'controls.lights': 'Lights',
  'controls.horn': 'Horn',
  'controls.belt': 'Seatbelt',
  'controls.camera': 'Switch camera',
  'controls.pause': 'Pause',
  'controls.kb.throttle': 'W or ↑',
  'controls.kb.brake': 'S or ↓',
  'controls.kb.steer': 'A / D or ← / →',
  'controls.kb.gears': 'Keys P, R, N, D',
  'controls.kb.handbrake': 'Space',
  'controls.kb.signals': 'Q / E',
  'controls.kb.glance': '1 / 2 / 3',
  'controls.kb.lights': 'L',
  'controls.kb.horn': 'H',
  'controls.kb.belt': 'B',
  'controls.kb.camera': 'C',
  'controls.kb.pause': 'Esc',
  'controls.gp.throttle': 'Right trigger (RT)',
  'controls.gp.brake': 'Left trigger (LT)',
  'controls.gp.steer': 'Left stick',
  'controls.gp.gears': 'D-pad ↑ / ↓',
  'controls.gp.handbrake': 'B button',
  'controls.gp.signals': 'LB / RB',
  'controls.gp.glance': 'Right stick',
  'controls.gp.lights': 'D-pad ←',
  'controls.gp.horn': 'X button',
  'controls.gp.belt': 'D-pad →',
  'controls.gp.camera': 'Y button',
  'controls.gp.pause': 'Start',
  'controls.wheel':
    'USB wheel & pedals (WebHID): on the roadmap. The input architecture is already prepared for it.',

  // Theory — Module 0
  'theory.title': 'Module 0 · Before you drive',
  'theory.intro':
    'Ten minutes of theory that will save you hours of bad habits. Read it once; the simulator will remind you while you drive.',
  'theory.seat.title': 'Seat, mirrors and seatbelt',
  'theory.seat.body':
    'Adjust the seat so you can press the brake fully without stretching. Set all three mirrors to cover as much as possible. Seatbelt ALWAYS before moving: in the simulator, key B.',
  'theory.mirrors.title': 'The three mirrors',
  'theory.mirrors.body':
    'Rear-view: what is behind? Left and right: what is beside you and in the blind spot? In the simulator you glance with 1 (left), 2 (rear-view) and 3 (right), and the examiner counts your glances.',
  'theory.dashboard.title': 'Dashboard and warning lights',
  'theory.dashboard.body':
    'Speedometer, rev counter, engaged gear and indicators: ABS (pulsing under hard braking), TC (traction control) and HOLD (hill hold). When a light blinks, the car is talking to you.',
  'theory.prnd.title': 'What P · R · N · D means',
  'theory.prnd.body':
    'P (Park) locks the transmission: only when stopped. R is reverse: never while rolling forward. N is neutral. D is drive. You must press the brake to shift out of P — the simulator enforces the same interlocks as a real car.',
  'theory.pedals.title': 'Two pedals, one foot',
  'theory.pedals.body':
    'In an automatic your right foot does everything: accelerator and brake. The left one ALWAYS rests. That makes pressing both impossible, and your braking will transfer to any car.',
  'theory.creep.title': 'The car "creeps" on its own',
  'theory.creep.body':
    'In D or R, when you release the brake, an automatic moves slowly without throttle. Use it to manoeuvre and park: control creep with the brake alone.',
  'theory.msm.title': 'Mirrors – Signal – Manoeuvre',
  'theory.msm.body':
    'The golden rule. First LOOK (rear-view and side mirror), then SIGNAL in good time, and only then MANOEUVRE. In that order, always. Examiners fail more people for not looking than for bad lines.',
  'theory.signals.title': 'Turn signals',
  'theory.signals.body':
    'Signal before turning, changing lanes or pulling out. In the simulator: Q left, E right. They self-cancel after the turn, like in a real car.',
  'theory.steering.title': 'The steering wheel',
  'theory.steering.body':
    'Look far ahead, where you want to go: hands follow eyes. Small, continuous corrections; enter too fast and the car understeers wide. Speed is decided BEFORE the corner.',
  'theory.lane.title': 'Lane position',
  'theory.lane.body':
    'Center the car between the lines. Reference: the left edge of the bonnet near the center line. If you touch a line, correct gently — never yank the wheel.',
  'theory.braking.title': 'Braking well',
  'theory.braking.body':
    'Brake in three phases: firm at first, hold, then EASE OFF just before stopping to avoid the jerk. If the ABS pulses under your foot, keep the pressure: it is working for you.',
  'theory.stopLine.title': 'Stopping at the line',
  'theory.stopLine.body':
    'A stop is complete (zero speed), nose on the line without crossing it. Count "one" at rest before moving on.',
  'theory.stopSign.title': 'STOP and yield',
  'theory.stopSign.body':
    'STOP: full stop required, always. Yield: slow down and stop only if someone is coming. In both, observation is what scores.',
  'theory.speed.title': 'Speed limits',
  'theory.speed.body':
    'The limit is a maximum, not a target. On practice grounds drive well below it. This simulator never rewards speed: it rewards control.',
  'theory.distance.title': 'Safe following distance',
  'theory.distance.body':
    'Two-second rule behind the vehicle ahead; double it in rain. Distance is your reaction time converted into meters.',
  'theory.reverse.title': 'Reversing',
  'theory.reverse.body':
    'Before engaging R: rear-view mirror, both side mirrors and a head check. Reverse at walking pace, guided by your mirrors. Tiny steering inputs: everything is amplified in reverse.',
  'theory.reverseCam.title': 'Camera and sensors',
  'theory.reverseCam.body':
    'The reversing camera helps but does not replace mirrors or the head check: it has blind spots. Treat it as one more reference.',
  'theory.priorities.title': 'Right of way',
  'theory.priorities.body':
    'Without signs, priority to the right. With signs, they rule. In roundabouts, traffic inside has priority. When in doubt: yield and always keep an escape route.',

  // Common
  'common.loading': 'Loading physics…',
  'common.close': 'Close',
  'common.km': 'km',
  'common.kmh': 'km/h',
  'common.mph': 'mph',
};
